import { useEffect, useState } from "react";
import { Plus, Loader2, Trash2, Copy, Check, Pencil, Link2, TrendingUp, KeyRound } from "lucide-react";
import api, { formatDetail, shortDate, fmtNum } from "@/lib/api";
import Modal from "@/components/Modal";
import PaymentLinksDrawer from "@/pages/admin/PaymentLinksDrawer";
import PerLinkAnalytics from "@/components/PerLinkAnalytics";
import FieldLockEditor from "@/components/FieldLockEditor";
import { ResetMfaButton } from "@/components/admin/ResetMfaButton";
import { useAuth } from "@/lib/auth";

export default function AdminPartners() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [created, setCreated] = useState(null);
    const [editing, setEditing] = useState(null);   // partner being edited
    const [linksFor, setLinksFor] = useState(null); // partner whose payment-links drawer is open
    const [analyticsFor, setAnalyticsFor] = useState(null);
    const load = async () => { const r = await api.get("/admin/partners"); setItems(r.data.items); };
    useEffect(() => { load(); }, []);
    const remove = async (pid) => {
        if (!window.confirm("Delete this partner and all their data?")) return;
        await api.delete(`/admin/partners/${pid}`); load();
    };
    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Partners</h1>
                    <p className="text-sm text-slate-500 mt-1">Onboard businesses and manage their access.</p>
                </div>
                <button data-testid="add-partner-btn" onClick={() => setOpen(true)} className="btn-primary px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Add partner
                </button>
            </div>

            <div className="card overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            <th className="px-4 py-3">Business</th>
                            <th className="px-4 py-3">Contact</th>
                            <th className="px-4 py-3">Link code</th>
                            <th className="px-4 py-3">Team</th>
                            <th className="px-4 py-3">Txns</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3">Created</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400 text-sm">No partners yet.</td></tr>}
                        {items.map((p) => (
                            <tr key={p.partner_id} data-testid={`partner-${p.partner_id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                                <td className="px-4 py-3 font-medium">{p.business_name}</td>
                                <td className="px-4 py-3 text-slate-600">{p.contact_email}</td>
                                <td className="px-4 py-3 font-mono text-xs">{p.link_code}</td>
                                <td className="px-4 py-3 font-mono">{fmtNum(p.team_size)}</td>
                                <td className="px-4 py-3 font-mono">{fmtNum(p.transactions_count)}</td>
                                <td className="px-4 py-3">
                                    <span className={`pill ${p.status === "active" ? "pill-success" : "pill-failed"}`}>{p.status}</span>
                                    {p.analytics_enabled && <span className="pill pill-info ml-1">PRO</span>}
                                    {p.aa_enabled && <span className="pill pill-pending ml-1" title="Bank Aggregator add-on">AA</span>}
                                </td>
                                <td className="px-4 py-3 text-slate-500 text-xs">{shortDate(p.created_at)}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button data-testid={`partner-analytics-${p.partner_id}`}
                                            title={p.analytics_enabled ? "View per-link analytics" : "Analytics gated — enable in Edit"}
                                            onClick={() => setAnalyticsFor(p)}
                                            className={`p-1.5 rounded mr-1 ${p.analytics_enabled ? "text-emerald-700 hover:bg-emerald-50" : "text-slate-300 hover:bg-slate-50"}`}>
                                        <TrendingUp className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`partner-links-${p.partner_id}`} title="Manage payment links"
                                            onClick={() => setLinksFor(p)}
                                            className="text-blue-600 hover:bg-blue-50 p-1.5 rounded mr-1">
                                        <Link2 className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`partner-edit-${p.partner_id}`} title="Edit business name"
                                            onClick={() => setEditing(p)}
                                            className="text-slate-600 hover:bg-slate-100 p-1.5 rounded mr-1">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`partner-delete-${p.partner_id}`} title="Delete"
                                            onClick={() => remove(p.partner_id)}
                                            className="text-rose-600 hover:bg-rose-50 p-1.5 rounded">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && <CreatePartnerModal onClose={() => setOpen(false)} onCreated={(c) => { setCreated(c); setOpen(false); load(); }} />}
            {created && <CredentialsModal data={created} onClose={() => setCreated(null)} />}
            {editing && <EditPartnerModal partner={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
            {linksFor && <PaymentLinksDrawer partner={linksFor} onClose={() => setLinksFor(null)} />}
            {analyticsFor && (
                <Modal testId="admin-analytics-modal" onClose={() => setAnalyticsFor(null)} size="xl"
                       title={`Analytics · ${analyticsFor.business_name}`}>
                    <PerLinkAnalytics
                        path={`/admin/partners/${analyticsFor.partner_id}/analytics`}
                        scope="admin"
                        upgradeCta="Analytics tier is OFF for this partner. Enable it in Edit → Analytics Pro tier to let them see this on their own dashboard."
                    />
                </Modal>
            )}
        </div>
    );
}

function EditPartnerModal({ partner, onClose, onSaved }) {
    const [f, setF] = useState({
        business_name: partner.business_name || "",
        contact_email: partner.contact_email || "",
        contact_phone: partner.contact_phone || "",
        status: partner.status || "active",
        analytics_enabled: partner.analytics_enabled || false,
        aa_enabled: partner.aa_enabled || false,
    });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try {
            await api.patch(`/admin/partners/${partner.partner_id}`, f);
            onSaved();
        } catch (e2) { setErr(formatDetail(e2?.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal testId="partner-edit-modal" onClose={onClose} title="Edit partner" size="md">
            <form onSubmit={submit} className="space-y-3">
                <div>
                    <label className="label">Business name</label>
                    <input data-testid="ep-business" required maxLength={120} className="input"
                           value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} />
                </div>
                <div>
                    <label className="label">Contact email</label>
                    <input data-testid="ep-cemail" required type="email" className="input"
                           value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
                </div>
                <div>
                    <label className="label">Contact phone</label>
                    <input data-testid="ep-phone" className="input"
                           value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} />
                </div>
                <div>
                    <label className="label">Status</label>
                    <select data-testid="ep-status" value={f.status} className="input"
                            onChange={(e) => setF({ ...f, status: e.target.value })}>
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                    </select>
                </div>
                <div className="border-t border-slate-200 pt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" data-testid="ep-analytics" checked={f.analytics_enabled}
                               onChange={(e) => setF({ ...f, analytics_enabled: e.target.checked })} />
                        <span className="text-sm">
                            <span className="font-medium">Analytics Pro tier</span>
                            <span className="block text-xs text-slate-500 mt-0.5">
                                Lets the partner see per-link earnings, conversion, top customer + UPI app on /partner/analytics.
                                Toggle off to gate behind a paid upgrade.
                            </span>
                        </span>
                    </label>
                </div>
                <div className="border-t border-slate-200 pt-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" data-testid="ep-aa" checked={f.aa_enabled}
                               onChange={(e) => setF({ ...f, aa_enabled: e.target.checked })} />
                        <span className="text-sm">
                            <span className="font-medium">Bank Aggregator (AA) add-on</span>
                            <span className="block text-xs text-slate-500 mt-0.5">
                                Unlocks RBI-compliant bank-statement fetch via Setu Account Aggregator on /partner/aa.
                                Toggle off to hide the feature entirely from this partner.
                            </span>
                        </span>
                    </label>
                </div>

                <PartnerFieldLocks partnerId={partner.partner_id} />
                {err && <div className="text-rose-600 text-sm">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="ep-submit" type="submit" disabled={busy}
                            className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function CreatePartnerModal({ onClose, onCreated }) {
    const [f, setF] = useState({ business_name: "", contact_email: "", contact_phone: "", admin_name: "", admin_email: "", admin_password: "" });
    const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setErr(""); setBusy(true);
        try { const r = await api.post("/admin/partners", f); onCreated(r.data); }
        catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        finally { setBusy(false); }
    };
    return (
        <Modal testId="partner-create-modal" onClose={onClose} title="Onboard a new partner" size="lg">
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><label className="label">Business name</label>
                    <input data-testid="pf-business" required className="input" value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} /></div>
                <div><label className="label">Contact email</label>
                    <input data-testid="pf-cemail" required type="email" className="input" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></div>
                <div><label className="label">Phone</label>
                    <input data-testid="pf-phone" className="input" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></div>
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Partner admin login</div>
                </div>
                <div><label className="label">Admin name</label>
                    <input data-testid="pf-admin-name" required className="input" value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} /></div>
                <div><label className="label">Admin email</label>
                    <input data-testid="pf-admin-email" required type="email" className="input" value={f.admin_email} onChange={(e) => setF({ ...f, admin_email: e.target.value })} /></div>
                <div className="col-span-2"><label className="label">Admin password</label>
                    <input data-testid="pf-admin-pw" required type="password" minLength={6} className="input" value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} /></div>
                {err && <div className="col-span-2 text-sm text-rose-600">{err}</div>}
                <div className="col-span-2 flex justify-end gap-2 mt-2">
                    <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 rounded-md text-sm">Cancel</button>
                    <button data-testid="pf-submit" type="submit" disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Create partner
                    </button>
                </div>
            </form>
        </Modal>
    );
}

function CredentialsModal({ data, onClose }) {
    const [copied, setCopied] = useState("");
    const copy = (k, v) => { navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); };
    return (
        <Modal testId="creds-modal" onClose={onClose} title="Partner created – save these credentials" size="lg">
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                ⚠ The API secret will be shown only once. Copy it now.
            </div>
            <div className="space-y-3 text-sm">
                {[
                    ["Link code", data.partner.link_code],
                    ["API key", data.partner.api_key],
                    ["API secret", data.api_secret],
                ].map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                        <div className="w-24 text-xs font-medium text-slate-500">{k}</div>
                        <input readOnly value={v} className="input font-mono text-xs flex-1" />
                        <button onClick={() => copy(k, v)} className="btn-secondary px-2.5 py-2 rounded-md">{copied === k ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}</button>
                    </div>
                ))}
            </div>
            <div className="mt-5 flex justify-end">
                <button onClick={onClose} className="btn-primary px-4 py-2 rounded-md text-sm">Done</button>
            </div>
        </Modal>
    );
}


function PartnerFieldLocks({ partnerId }) {
    const { user: me } = useAuth();
    const isSuper = me?.role === "super_admin";
    const [users, setUsers] = useState([]);
    const [openUserId, setOpenUserId] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/partners/${partnerId}/users`);
            setUsers(r.data.items || []);
        } catch (e) {
            // Non-fatal: field-lock list renders an empty state below.
            // Log so an admin can see WHY they see no users (bad partner_id,
            // network flake, etc.) via DevTools.
            console.warn("[admin-partners] users fetch failed:", e?.response?.status, e?.message);
        }
        finally { setLoading(false); }
    };
    useEffect(() => { reload();   }, [partnerId]);

    return (
        <div className="border-t border-slate-200 pt-3" data-testid="admin-partner-field-locks">
            <div className="flex items-center gap-2 mb-2">
                <KeyRound className="w-4 h-4 text-amber-600" />
                <div className="text-sm font-medium">Edit-lock policy (per user)</div>
            </div>
            <div className="text-[11px] text-slate-500 mb-2">
                Restrict specific fields on specific pages behind a master code for any user under this partner.
                Useful for trust-but-verify workflows (e.g. only the partner_admin can edit transaction amounts).
            </div>
            {loading ? (
                <div className="text-xs text-slate-400">Loading users…</div>
            ) : (
                <div className="space-y-1.5">
                    {users.map((u) => (
                        <div key={u.user_id} data-testid={`admin-fl-row-${u.user_id}`}>
                            <div className="flex items-stretch gap-2">
                            <button type="button" onClick={() => setOpenUserId(openUserId === u.user_id ? null : u.user_id)}
                                    className="flex-1 flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-3 py-2 text-xs hover:bg-slate-100">
                                <div className="text-left">
                                    <div className="font-medium text-slate-800">{u.name} <span className="text-[10px] text-slate-400 font-normal ml-1">({u.role})</span></div>
                                    <div className="text-slate-500">{u.email}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {u.has_master_code
                                        ? <span className="text-emerald-700 text-[10px]">code set</span>
                                        : <span className="text-slate-400 text-[10px]">no code</span>}
                                    {u.locked_pages > 0 && (
                                        <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded">{u.locked_pages} page{u.locked_pages === 1 ? "" : "s"}</span>
                                    )}
                                </div>
                            </button>
                            {isSuper && (
                                <div className="flex items-center gap-2 shrink-0">
                                    <ResetMfaButton userType="partner" user={u} onDone={reload} />
                                </div>
                            )}
                            </div>
                            {openUserId === u.user_id && (
                                <div className="bg-white border border-slate-200 rounded-b -mt-px p-3">
                                    <FieldLockEditor scope="admin" userId={u.user_id} onSaved={reload} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
